/**
 * Campaign performance against targets — the derived figures behind the
 * Results page's Performance tab (the marketing manager's "Perf - Email"
 * spreadsheet, moved into CIS). Rates, leads, cost per lead and ROI are
 * computed here from the stored actuals and costs, never stored.
 * See docs/features/campaign-performance-targets/01_product_brief.md.
 */

import type {
  CampaignCost,
  CampaignCostType,
  CampaignPerformance,
  PerformanceFigures,
  PerformanceTargets,
  SendPerformance,
} from "../data/marketing-types";
import { type SendLabelInput, sendLabel } from "./campaign-sends";
import { formatCurrencyGbp } from "./formatters";

export const DEFAULT_PERFORMANCE_TARGETS: PerformanceTargets = {
  open_rate: 0.3,
  click_rate: 0.025,
  max_unsubscribe_rate: 0.0015,
};

export const COST_TYPE_LABELS: Record<CampaignCostType, string> = {
  marketing: "Marketing cost",
  design: "Design cost",
  production: "Production cost",
};

export type TargetStatus = "met" | "missed";

export interface PerformanceRates {
  /** Delivered ÷ Contacts. */
  delivered: number | null;
  /** Opened ÷ Delivered. */
  opened: number | null;
  /** Clicked ÷ Delivered. */
  clicked: number | null;
  /** Unsubscribed ÷ Contacts. */
  unsubscribed: number | null;
  /** Leads ÷ Contacts (leads = unique clickers). */
  response: number | null;
  /** New services ÷ Sales (number of). */
  new_services: number | null;
}

function ratio(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null;
}

export function performanceRates(
  figures: PerformanceFigures,
): PerformanceRates {
  return {
    delivered: ratio(figures.delivered, figures.contacts),
    opened: ratio(figures.opened, figures.delivered),
    clicked: ratio(figures.clicked, figures.delivered),
    unsubscribed: ratio(figures.unsubscribed, figures.contacts),
    response: ratio(figures.clicked, figures.contacts),
    new_services: ratio(figures.new_services_count, figures.sales_count),
  };
}

/** Opened and Clicked must reach their target; Unsubscribed must stay at or
 *  under its maximum. Null when there's no rate to judge. */
export function targetStatuses(
  rates: PerformanceRates,
  targets: PerformanceTargets,
): {
  opened: TargetStatus | null;
  clicked: TargetStatus | null;
  unsubscribed: TargetStatus | null;
} {
  const atLeast = (rate: number | null, target: number) =>
    rate === null ? null : rate >= target ? "met" : "missed";
  return {
    opened: atLeast(rates.opened, targets.open_rate),
    clicked: atLeast(rates.clicked, targets.click_rate),
    unsubscribed:
      rates.unsubscribed === null
        ? null
        : rates.unsubscribed <= targets.max_unsubscribe_rate
          ? "met"
          : "missed",
  };
}

function toSendLabelInput(send: SendPerformance): SendLabelInput {
  return {
    id: send.step_id,
    order: send.position,
    follows_step_id: send.follows_step_id,
  };
}

/** A send row's name, by the same rule as the Commercial outcomes rows:
 *  "Tranche N" / "Tranche N Reminder" once a campaign has 2+ email sends,
 *  "Email" for a lone one. */
export function performanceSendLabel(
  send: SendPerformance,
  sends: SendPerformance[],
): string {
  if (sends.length < 2) return "Email";
  return sendLabel(toSendLabelInput(send), sends.map(toSendLabelInput));
}

/** The costs behind the Summary to date's ROI on uplift: only campaigns
 *  whose uplift the summary counts (computable and a fair comparison), so
 *  the return and the cost cover the same campaigns. */
export function upliftCampaignCosts(campaigns: CampaignPerformance[]): number {
  return campaigns
    .filter(
      (c) => c.figures.uplift_amount !== null && c.figures.uplift_is_comparable,
    )
    .reduce((sum, c) => sum + campaignCostTotal(c.costs), 0);
}

/** A campaign's total costs (every cost, tied or not). */
export function campaignCostTotal(costs: CampaignCost[]): number {
  return costs.reduce((sum, cost) => sum + cost.amount, 0);
}

/** A send's costs: only those tied to it. Untied campaign costs are not
 *  spread across sends — they count at campaign level only. Null when no
 *  cost is tied to the send, so cost-based figures show "—". */
export function sendCostTotal(
  costs: CampaignCost[],
  stepId: string,
): number | null {
  const tied = costs.filter((cost) => cost.step_id === stepId);
  return tied.length > 0 ? campaignCostTotal(tied) : null;
}

export interface CostReturns {
  costPerLead: number | null;
  /** (Sales £ − Costs) ÷ Costs. */
  roiOnSales: number | null;
  /** (Uplift − Costs) ÷ Costs; null when uplift isn't usable. */
  roiOnUplift: number | null;
}

export function costReturns(
  figures: PerformanceFigures,
  totalCost: number | null,
): CostReturns {
  if (totalCost === null || totalCost <= 0) {
    return { costPerLead: null, roiOnSales: null, roiOnUplift: null };
  }
  const upliftUsable =
    figures.uplift_amount !== null && figures.uplift_is_comparable;
  return {
    costPerLead: ratio(totalCost, figures.clicked),
    roiOnSales: (figures.sales_amount - totalCost) / totalCost,
    roiOnUplift: upliftUsable
      ? ((figures.uplift_amount as number) - totalCost) / totalCost
      : null,
  };
}

/** "40.2%" — one decimal place, or two below 1% so an unsubscribe rate of
 *  0.15% doesn't round to "0.2%". "—" when there's no rate. */
export function formatRate(rate: number | null): string {
  if (rate === null) return "—";
  const pct = rate * 100;
  const dp = Math.abs(pct) < 1 && pct !== 0 ? 2 : 1;
  return `${pct.toFixed(dp)}%`;
}

/** ROI as a signed whole percentage: "+300%", "−836%". "—" when there's no
 *  cost to measure against. */
export function formatRoi(roi: number | null): string {
  if (roi === null) return "—";
  const pct = Math.round(roi * 100);
  if (pct === 0) return "0%";
  // U+2212 minus, matching formatUplift.
  return `${pct > 0 ? "+" : "−"}${Math.abs(pct).toLocaleString("en-GB")}%`;
}

export function formatCostPerLead(value: number | null): string {
  return value === null ? "—" : formatCurrencyGbp(value, 2);
}
